import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../hooks/useLanguage';

export function TopbarLanguageSwitcher() {
  const { t } = useTranslation();
  const { currentLang, setLanguage } = useLanguage();
  // Language names stay in their native form — "Nederlands" / "English" —
  // regardless of the current UI language, so each option is legible to the
  // speaker of that language. (Don't route these through t().)
  const options = [
    { lang: 'nl' as const, flag: '🇳🇱', label: 'Nederlands' },
    { lang: 'en' as const, flag: '🇬🇧', label: 'English' },
  ];
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const current = options.find((o) => o.lang === currentLang) ?? options[0];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-text-secondary hover:bg-subtle flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium transition-colors duration-150"
        aria-label={t('settings.switchLanguage')}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="text-xs font-semibold tracking-wide uppercase">{current.lang}</span>
        <ChevronDown
          className={`h-3 w-3 opacity-50 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-border bg-card absolute top-full right-0 z-50 mt-1.5 w-36 overflow-hidden rounded-xl border shadow-lg shadow-black/5">
          {options.map((option) => (
            <button
              key={option.lang}
              onClick={() => {
                setLanguage(option.lang);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-sm transition-colors duration-100 ${
                currentLang === option.lang
                  ? 'bg-accent/10 text-accent font-medium'
                  : 'text-text-secondary hover:bg-subtle'
              }`}
            >
              <span className="text-base">{option.flag}</span>
              <span>{option.label}</span>
              {currentLang === option.lang && <Check className="ml-auto h-3.5 w-3.5" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
